import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiGet, apiPost, apiPut, apiDelete } from "../api";

export interface Category {
  id: number;
  name: string;
  description: string;
  active: boolean;
  created_at: Dayjs;
  updated_at: Dayjs;
}

export interface CategoryListResponse {
  items: Category[];
  total: number;
  page: number;
  per_page: number;
}

export interface CategoryListParams {
  q?: string;
  page?: number;
  per_page?: number;
}

function qs(params: CategoryListParams): string {
  const u = new URLSearchParams();
  if (params.q) u.set("q", params.q);
  if (params.page) u.set("page", String(params.page));
  if (params.per_page) u.set("per_page", String(params.per_page));
  const s = u.toString();
  return s ? "?" + s : "";
}

export function useCategoryList(params: CategoryListParams = {}) {
  return useQuery<CategoryListResponse>({
    queryKey: ["categories", params],
    queryFn: () => apiGet<CategoryListResponse>("/categories" + qs(params)),
  });
}

export interface CategoryInput {
  name: string;
  description?: string;
  active?: boolean;
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CategoryInput) => apiPost<Category>("/categories", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: CategoryInput & { id: number }) =>
      apiPut<Category>(`/categories/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiDelete<void>(`/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });
}
